import { useCallback, useEffect } from 'react';

import { PAGE_TYPES, Page } from '../enums';
import { useSelectedItemsStore } from '../store';
import { useUserCommunicationsStore } from '../store/Inbox/UserCommunications';

export function useToggleItemSelectionPersistent<
  T extends { messageId: string },
>(items: T[] = [], page?: Page) {
  const {
    clearSelection,
    selectedItems,
    setAllMailItems,
    setDraftItems,
    setInboxItems,
    setSentItems,
    setSpamItems,
    setTrashItems,
    setLabelsItems,
  } = useSelectedItemsStore();

  const pageActions: Record<
    Page,
    {
      get: () => string[];
      set: (items: string[]) => void;
    }
  > = {
    [PAGE_TYPES.INBOX]: {
      get: () => selectedItems.inboxItems,
      set: setInboxItems,
    },
    [PAGE_TYPES.SENT]: {
      get: () => selectedItems.sentItems,
      set: setSentItems,
    },
    [PAGE_TYPES.DRAFT]: {
      get: () => selectedItems.draftItems,
      set: setDraftItems,
    },
    [PAGE_TYPES.SPAM]: {
      get: () => selectedItems.spamItems,
      set: setSpamItems,
    },
    [PAGE_TYPES.TRASH]: {
      get: () => selectedItems.trashItems,
      set: setTrashItems,
    },
    [PAGE_TYPES.ALL]: {
      get: () => selectedItems.allMailItems,
      set: setAllMailItems,
    },
    [PAGE_TYPES.LABELS]: {
      get: () => selectedItems.labelsItems,
      set: setLabelsItems,
    },
    [PAGE_TYPES.DOBOX]: {
      get: () => [],
      set: () => undefined,
    },
    [PAGE_TYPES.ARCHIVE]: {
      get: () => [],
      set: () => undefined,
    },
    [PAGE_TYPES.SNOOZED]: {
      get: () => [],
      set: () => undefined,
    },
    [PAGE_TYPES.UNKNOWN]: {
      get: () => [],
      set: () => undefined,
    },
  };

  const renderedIds = new Set(items.map((item) => item.messageId));
  const selectedItemsZustand = new Set(
    (page ? pageActions[page].get() : []).filter((id) => renderedIds.has(id)),
  );

  const saveSelection = useCallback(
    (newSelection: Set<string>) => {
      if (!page) return;
      pageActions[page].set(Array.from(newSelection));
    },
    [page, pageActions],
  );

  const toggleItemSelection = useCallback(
    (messageId: string) => {
      const newSelection = new Set(selectedItemsZustand);
      if (newSelection.has(messageId)) {
        newSelection.delete(messageId);
      } else {
        newSelection.add(messageId);
      }
      saveSelection(newSelection);
    },
    [saveSelection, selectedItemsZustand],
  );

  const toggleSelectAll = useCallback(() => {
    if (!page) return;

    const itemsLength = items.length;
    if (selectedItemsZustand.size === itemsLength) {
      clearSelection();
    } else {
      const allMessageIds = new Set(items.map((item) => item.messageId));
      saveSelection(allMessageIds);
    }
  }, [items, page, selectedItemsZustand, clearSelection, saveSelection]);

  const clearSelectionPage = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  useEffect(() => {
    if (!page) return;

    const unsubscribe = useUserCommunicationsStore.subscribe((state) => {
      const getStoreMessageIds = () => {
        switch (page) {
          case PAGE_TYPES.INBOX:
            return new Set([
              ...state.primary.map((msg) => msg.messageId),
              ...state.promotions.map((msg) => msg.messageId),
              ...state.social.map((msg) => msg.messageId),
              ...state.updates.map((msg) => msg.messageId),
              ...state.forums.map((msg) => msg.messageId),
            ]);
          case PAGE_TYPES.SENT:
            return new Set(state.sent.map((msg) => msg.messageId));
          case PAGE_TYPES.DRAFT:
            return new Set(state.draft.map((msg) => msg.messageId));
          case PAGE_TYPES.SPAM:
            return new Set(state.spam.map((msg) => msg.messageId));
          case PAGE_TYPES.TRASH:
            return new Set(state.trash.map((msg) => msg.messageId));
          case PAGE_TYPES.ALL:
            return new Set(state.all.map((msg) => msg.messageId));
          case PAGE_TYPES.LABELS:
            return new Set(
              Object.values(state.labels)
                .flat()
                .map((msg) => msg.messageId),
            );
          default:
            return new Set();
        }
      };

      const storeMessageIds = getStoreMessageIds();
      const currentSelectedItems = pageActions[page].get();

      const itemsToRemove = currentSelectedItems.filter(
        (messageId) => !storeMessageIds.has(messageId),
      );

      if (itemsToRemove.length > 0) {
        const newSelection = currentSelectedItems.filter((messageId) =>
          storeMessageIds.has(messageId),
        );
        pageActions[page].set(newSelection);
      }
    });

    return unsubscribe;
  }, [page, pageActions]);

  const isAllSelected =
    items.length > 0 && selectedItemsZustand.size === items.length;
  const hasSelectedItems = selectedItemsZustand.size > 0;

  return {
    selectedItems: selectedItemsZustand,
    toggleItemSelection,
    toggleSelectAll,
    clearSelection: clearSelectionPage,
    isAllSelected,
    hasSelectedItems,
  };
}
